import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment, { Moment } from 'moment-timezone';
import clsx from 'clsx';
import ReactDatePicker from 'react-datetime';
import FormGroup from '../Forms/Group';
import FormLabel from '../Forms/Label';
import FormInput from '../Forms/Input';
import Button from '../Button';
import { FormErrors } from '../../../app/errors/FormErrors';
import { INewPayment } from '../../../app/stores/invoices/interfaces/payments/INewPayment';
import { IResponse } from '../../../app/services/api/IResponse';
import { IPayment } from '../../../app/stores/payments/interfaces/IPayment';
import { DataInvalidError } from '../../../app/errors/DataInvalidError';
import { DetailedMessageError } from '../../../app/errors/DetailedMessageError';
import { PaymentStore } from '../../../app/stores/payments/PaymentStore';
import { NotificationStore } from '../../../app/stores/notifications/NotificationStore';
import { INotification } from '../../../app/stores/notifications/interfaces/INotification';
import Notification from '../Notification';
import FormMessage from '../Forms/Message';
import { IPaymentMethod } from '../../../app/stores/payment-methods/interfaces/IPaymentMethod';
import { PaymentMethodStore } from '../../../app/stores/payment-methods/PaymentMethodStore';
import { UserStore } from '../../../app/stores/user/UserStore';
import FormSelect from '../Forms/Select';
import { IListPayment } from '../../../app/stores/payments/interfaces/IListPayment';
import { AuthStore } from '../../../app/stores/auth/AuthStore';

export interface EditPaymentModelProps {
    payment: IListPayment;
    paymentStore?: PaymentStore;
    notificationStore?: NotificationStore;
    onPaymentUpdated: () => void;
    paymentMethodStore?: PaymentMethodStore;
    userStore?: UserStore;
    authStore?: AuthStore;
}

@inject('invoiceStore', 'userStore', 'paymentStore', 'notificationStore', 'paymentMethodStore', 'authStore')
@observer
class EditPaymentModel extends React.Component<EditPaymentModelProps, unknown> {
    public static NOTIFICATION_NAMESPACE = 'edit_payment';

    @observable private formErrors: FormErrors;
    @observable private formValues: INewPayment = {
        amount: '',
        paid_at: moment().format(),
        payment_method_uuid: ''
    };
    @observable private paymentMethods: IPaymentMethod[] = [];

    public constructor(props: EditPaymentModelProps) {
        super(props);

        this.formErrors = new FormErrors();
    }

    public componentDidMount() {
        const { paymentMethodStore, userStore, payment } = this.props;

        this.formValues = {
            amount: payment.amount,
            paid_at: payment.paid_at,
            payment_method_uuid: payment.payment_method.uuid
        };

        if (userStore?.currentCompany) {
            paymentMethodStore?.getPaymentMethods(
                userStore.currentCompany.company.uuid
            ).then((paymentMethods: IPaymentMethod[]) => {
                this.paymentMethods = paymentMethods;
            });
        }
    }

    private submitPayment(event: React.FormEvent): void {
        event.preventDefault();

        const {
            paymentStore,
            notificationStore,
            onPaymentUpdated,
            payment
        } = this.props;

        notificationStore?.clear(EditPaymentModel.NOTIFICATION_NAMESPACE);
        notificationStore?.clear();
        this.formErrors.clear();

        paymentStore?.editPayment(
            payment.uuid,
            this.formValues
        ).then((updatedPayment: IResponse<IPayment, { rest_amount: string }> | undefined) => {
            if (updatedPayment) {
                notificationStore?.addNotification({
                    variant: 'success',
                    message: 'Betaling succesvol aangepast',
                    // eslint-disable-next-line max-len
                    messageDetails: `Het openstaande bedrag van factuur ${payment.invoice_number} is nu €${updatedPayment.rest_amount}`
                });
                onPaymentUpdated();
            }
        }).catch((e: DataInvalidError | DetailedMessageError): void => {
            if (e instanceof DataInvalidError) {
                this.formErrors.set(e.errors);
                notificationStore?.addNotification({
                    variant: 'danger',
                    message: e.message,
                    namespace: EditPaymentModel.NOTIFICATION_NAMESPACE
                });
            } else {
                notificationStore?.addNotification({
                    variant: 'danger',
                    message: e.message,
                    messageDetails: e.details,
                    namespace: EditPaymentModel.NOTIFICATION_NAMESPACE
                });
            }
        });
    }

    public render(): React.ReactNode {
        const { notificationStore, userStore } = this.props;

        return (
            <>
                <div className="mb-4">
                    {notificationStore?.all(
                        EditPaymentModel.NOTIFICATION_NAMESPACE
                    ).map((notification: INotification) => (
                        <Notification key={notification.id} {...notification} />
                    ))}
                </div>
                <form onSubmit={(event: React.FormEvent) => this.submitPayment(event)}>
                    <FormGroup>
                        <FormLabel>Bedrag</FormLabel>
                        <FormInput
                            value={this.formValues.amount}
                            type="text"
                            subType="currency"
                            name="amount"
                            hasError={this.formErrors.has('amount')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                this.formValues.amount = event.target.value;
                            }}
                        />
                        {this.formErrors.has('amount') && (
                            <FormMessage type="invalid">
                                {this.formErrors.first('amount')}
                            </FormMessage>
                        )}
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Betaald op</FormLabel>
                        <ReactDatePicker
                            className={clsx(
                                'form-control-container w-full',
                                this.formErrors.has('paid_at') && 'has-error'
                            )}
                            inputProps={{
                                className: 'form-control',
                                name: 'paid_at',
                                autoComplete: 'off'
                            }}
                            locale="nl"
                            closeOnSelect
                            timeFormat={false}
                            displayTimeZone={userStore?.currentCompany?.company.timezone.name.replace(' ', '_')}
                            onChange={(date: Moment | string) => {
                                if (typeof date !== 'string') {
                                    this.formValues.paid_at = date.format();
                                }
                            }}
                            value={this.formValues.paid_at
                                ? moment(this.formValues.paid_at)
                                : undefined}
                        />
                        {this.formErrors.has('paid_at') && (
                            <FormMessage type="invalid">
                                {this.formErrors.first('paid_at')}
                            </FormMessage>
                        )}
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Betalingsmethode</FormLabel>
                        <FormSelect
                            name="payment_method_uuid"
                            value={this.formValues.payment_method_uuid}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                this.formValues.payment_method_uuid = event.target.value;
                            }}
                            hasError={this.formErrors.has('payment_method_uuid')}
                        >
                            <option value="">Kiezen...</option>
                            {this.paymentMethods.map((paymentMethod: IPaymentMethod) => (
                                <option
                                    key={paymentMethod.uuid}
                                    value={paymentMethod.uuid}
                                >
                                    {paymentMethod.name}
                                </option>
                            ))}
                        </FormSelect>
                        {this.formErrors.has('payment_method_uuid') && (
                            <FormMessage type="invalid">
                                {this.formErrors.first('payment_method_uuid')}
                            </FormMessage>
                        )}
                    </FormGroup>
                    <Button
                        variant="primary"
                        type="submit"
                    >
                        Opslaan
                    </Button>
                </form>
            </>
        );
    }
}

export default EditPaymentModel;
